import { HighlightOff, Search } from '@mui/icons-material';
import { Box, IconButton, InputAdornment, TextField, TextFieldProps, Typography, useTheme } from '@mui/material';
import React, { useEffect, useMemo, useRef } from 'react';
import { getClientOS } from 'utils/getClientOS';

export type SearchBarProps = TextFieldProps & {
    placeholder?: string;
    hideKeys?: boolean;
    placeholderShouldIncludekeys?: boolean;
    clearSearch?: () => void;
    searchInput?: string;
};

export const SearchBar = ({
    InputProps,
    placeholder,
    hideKeys,
    placeholderShouldIncludekeys,
    searchInput,
    clearSearch,
    ...props
}: SearchBarProps) => {
    const theme = useTheme();
    const searchFieldRef = useRef<HTMLInputElement | null>(null);

    const keys = useMemo(() => {
        const os = getClientOS();
        if (os === 'MAC') return '⌘ + K';
        return 'Ctrl + K';
    }, []);

    const onFind = (e: KeyboardEvent) => {
        if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            console.log('Se presionó Command + K o Ctrl + K');
            searchFieldRef.current?.focus();
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', onFind);

        return () => {
            document.removeEventListener('keydown', onFind);
        };
    });
    return (
        <TextField
            {...props}
            value={searchInput}
            inputRef={searchFieldRef}
            variant="filled"
            InputProps={{
                hiddenLabel: true,
                disableUnderline: true,
                startAdornment: (
                    <Search
                        htmlColor="#858585"
                        sx={{
                            mr: 1,
                            '.Mui-focused &': {
                                color: theme.palette.primary.dark
                            }
                        }}
                    />
                ),
                endAdornment: (
                    <InputAdornment className="input-hot-keys" position="end" sx={{ height: '30px', transform: 'translateX(6px)' }}>
                        {searchInput && searchInput.length > 0 && (
                            <IconButton onClick={clearSearch}>
                                <HighlightOff sx={{ color: theme.palette.primary.dark }} />
                            </IconButton>
                        )}
                        {!hideKeys && (
                            <Box
                                sx={{
                                    bgcolor: 'white',
                                    borderRadius: '50px',
                                    px: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: '100%'
                                }}
                            >
                                <Typography color="#858585" fontSize="14px" fontWeight={500}>
                                    {keys}
                                </Typography>
                            </Box>
                        )}
                    </InputAdornment>
                ),
                placeholder: placeholderShouldIncludekeys
                    ? `${placeholder || 'Find in current page'} - ${keys}`
                    : placeholder || 'Find in current page',
                ...(InputProps || {}),
                sx: {
                    borderRadius: '50px',
                    '&.Mui-focused': {
                        backgroundColor: theme.palette.secondary.light,
                        '& .MuiInputAdornment-root .MuiSvgIcon-root': {
                            color: theme.palette.primary.dark
                        }
                    },
                    ...(InputProps?.sx ?? {})
                }
            }}
        />
    );
};
